import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from 'components/AppIcon';
import FooterSection from '../landing-page/components/FooterSection';
import GoBackButton from '../../components/GoBackButton';
import { useLanguage } from '../../contexts/LanguageContext';

const HelpSupport = () => {
  const { t, language } = useLanguage();
  const [activeRole, setActiveRole] = useState('buyer');

  const roles = [
    { id: 'buyer', label: t('buyer'), icon: 'Search', color: '#00D4FF' },
    { id: 'seller', label: t('seller'), icon: 'Home', color: '#7B2FFF' },
    { id: 'agent', label: t('agent'), icon: 'Briefcase', color: '#00D4FF' },
    { id: 'professional', label: t('professional'), icon: 'Award', color: '#7B2FFF' },
  ];

  const guideContent = {
    buyer: {
      title: language === 'bn' ? 'ক্রেতার গাইড' : 'Buyer Guides',
      description: language === 'bn' ? 'আপনার আদর্শ সম্পত্তি খুঁজে পেতে, বিশ্লেষণ করতে এবং নিশ্চিত করতে যা প্রয়োজন সব কিছু।' : 'Everything you need to find, analyse, and secure your perfect property.',
      guides: [
        {
          icon: 'Search',
          title: language === 'bn' ? 'সম্পত্তি অনুসন্ধান' : 'Searching for Properties',
          steps: language === 'bn' ? [
            'হোমপেজের সার্চ বার ব্যবহার করুন বা /search-results-এ নেভিগেট করুন',
            'ফিল্টার প্রয়োগ করুন: সম্পত্তির ধরন, মূল্য পরিসীমা, শয়নকক্ষ, বাথরুম, অঞ্চল',
            'অঞ্চল নির্বাচক ব্যবহার করে অস্ট্রেলিয়া এবং UAE বাজারের মধ্যে টগল করুন',
            'নতুন মিলে যাওয়া সম্পত্তি তালিকাভুক্ত হলে সতর্কতা পেতে অনুসন্ধান সংরক্ষণ করুন',
            'অবস্থান অনুযায়ী সম্পত্তি অন্বেষণ করতে ম্যাপ ভিউ ব্যবহার করুন',
          ] : [
            'Use the Search bar on the homepage or navigate to /search-results',
            'Apply filters: property type, price range, bedrooms, bathrooms, region',
            'Toggle between Australia and UAE markets using the region selector',
            'Save searches to receive alerts when new matching properties are listed',
            'Use the map view to explore properties by location',
          ]
        },
        {
          icon: 'Brain',
          title: language === 'bn' ? 'এআই বিশ্লেষণ বোঝা' : 'Understanding AI Analysis',
          steps: language === 'bn' ? [
            'ড্যাশবোর্ডে যেকোনো সম্পত্তির তালিকা URL পেস্ট করুন বা ম্যানুয়ালি তথ্য দিন',
            'এআই তুলনামূলক বিক্রয় এবং বাজার ডেটা বিশ্লেষণ করতে ১৫-৪৫ সেকেন্ড অপেক্ষা করুন',
            'রায় (অতিমূল্যায়িত / ন্যায্য / কম মূল্যায়িত) এবং আস্থার স্কোর পর্যালোচনা করুন',
            'আলোচনার জন্য প্রস্তাবিত মূল্য পরিসীমা — অফার ব্যান্ড অন্বেষণ করুন',
            'মূল সম্পত্তির অন্তর্দৃষ্টির জন্য শক্তি এবং ঝুঁকি বিভাগ দেখুন',
          ] : [
            'Paste any property listing URL or enter details manually in the Dashboard',
            'Wait 15–45 seconds for the AI to analyse comparable sales and market data',
            'Review the Verdict (Overpriced / Fair / Underpriced) and confidence score',
            'Explore the Offer Band — the recommended price range for negotiation',
            'Check the Strengths and Risks sections for key property insights',
          ]
        },
        {
          icon: 'BarChart3',
          title: language === 'bn' ? 'তুলনা সরঞ্জাম ব্যবহার' : 'Using Comparison Tools',
          steps: language === 'bn' ? [
            'তুলনা বোতাম ব্যবহার করে অনুসন্ধান ফলাফল থেকে সর্বোচ্চ ৪টি সম্পত্তি নির্বাচন করুন',
            'পাশাপাশি তুলনা দেখতে সম্পত্তি তুলনায় নেভিগেট করুন',
            'প্রতি বর্গমিটার মূল্য, বৈশিষ্ট্য, অবস্থান স্কোর এবং এআই রায় তুলনা করুন',
            'অফলাইন পর্যালোচনা বা পরামর্শদাতাদের সাথে শেয়ার করতে PDF হিসেবে রপ্তানি করুন',
          ] : [
            'Select up to 4 properties from search results using the Compare button',
            'Navigate to Property Comparison to see a side-by-side breakdown',
            'Compare price per sqm, features, location scores, and AI verdicts',
            'Export comparison as PDF for offline review or sharing with advisors',
          ]
        },
        {
          icon: 'DollarSign',
          title: language === 'bn' ? 'অফার কৌশল' : 'Offer Strategy',
          steps: language === 'bn' ? [
            'সম্পত্তি বিশ্লেষণ সম্পন্ন করার পরে অফার রেঞ্জ গাইডেন্স টুল ব্যবহার করুন',
            'বাজার ডেটার উপর ভিত্তি করে এআই-প্রস্তাবিত অফার ব্যান্ড পর্যালোচনা করুন',
            'বাজার-নির্দিষ্ট কৌশলের জন্য আলোচনা কৌশল বিভাগ দেখুন',
            'স্ট্যাম্প ডিউটি সহ মোট ক্রয় খরচ বুঝতে খরচ বিশ্লেষণ টুল ব্যবহার করুন',
          ] : [
            'Use the Offer Range Guidance tool after completing a property analysis',
            'Review the AI-suggested offer band based on market data',
            'Check the Negotiation Strategy section for market-specific tactics',
            'Use the Cost Breakdown tool to understand total purchase costs including stamp duty',
          ]
        },
      ]
    },
    seller: {
      title: language === 'bn' ? 'বিক্রেতার গাইড' : 'Seller Guides',
      description: language === 'bn' ? 'আকর্ষণীয় তালিকা তৈরি করুন, সঠিক মূল্য নির্ধারণ করুন এবং আপনার লিড কার্যকরভাবে পরিচালনা করুন।' : 'Create compelling listings, price correctly, and manage your leads effectively.',
      guides: [
        {
          icon: 'PlusCircle',
          title: language === 'bn' ? 'আপনার তালিকা তৈরি করা' : 'Creating Your Listing',
          steps: language === 'bn' ? [
            'নেভিগেশন মেনু থেকে "আপনার সম্পত্তি তালিকাভুক্ত করুন" ক্লিক করুন',
            'আপনার ভূমিকা নির্বাচন করুন: মালিক হিসেবে ব্যক্তিগতভাবে বা মালিকের পক্ষে এজেন্ট',
            'সম্পত্তির ঠিকানা দিন — আমরা স্বয়ংক্রিয়ভাবে এলাকা এবং রাজ্যের ডেটা পূরণ করব',
            'সম্পত্তির মূল তথ্য সম্পূর্ণ করুন: ধরন, শয়নকক্ষ, বাথরুম, পার্কিং, জমির আকার',
            'বৈশিষ্ট্য যোগ করুন এবং গল্পের প্রশ্নের উত্তর দিন যাতে এআই আকর্ষণীয় কপি তৈরি করতে পারে',
          ] : [
            'Click "List Your Property" from the navigation menu',
            'Select your role: Owner listing privately or Agent listing on behalf of owner',
            'Enter the property address — we\'ll auto-fill suburb and state data',
            'Complete property basics: type, bedrooms, bathrooms, parking, land size',
            'Add features and answer story questions to help AI generate compelling copy',
          ]
        },
        {
          icon: 'Camera',
          title: language === 'bn' ? 'ফটো অপ্টিমাইজেশন' : 'Photo Optimisation',
          steps: language === 'bn' ? [
            'সেরা তালিকার কার্যক্ষমতার জন্য কমপক্ষে ৮টি ফটো আপলোড করুন',
            'আলো উন্নত করতে এবং অগোছালো জিনিস সরাতে আমাদের এআই ফটো এনহান্সমেন্ট ব্যবহার করুন',
            'সেরা প্রদর্শনের মানের জন্য ফটোগুলি কমপক্ষে ১২০০×৮০০px হওয়া নিশ্চিত করুন',
            'অন্তর্ভুক্ত করুন: বাইরের অংশ, বসার এলাকা, রান্নাঘর, শয়নকক্ষ, বাথরুম, বাইরের জায়গা',
            'পাওয়া গেলে ফ্লোর প্ল্যান যোগ করুন — ফ্লোর প্ল্যান সহ তালিকাগুলি ৪০% বেশি জিজ্ঞাসা পায়',
          ] : [
            'Upload a minimum of 8 photos for best listing performance',
            'Use our AI Photo Enhancement to improve lighting and remove clutter',
            'Ensure photos are at least 1200×800px for best display quality',
            'Include: exterior, living areas, kitchen, bedrooms, bathrooms, outdoor spaces',
            'Add a floor plan if available — listings with floor plans get 40% more enquiries',
          ]
        },
        {
          icon: 'TrendingUp',
          title: language === 'bn' ? 'আপনার সম্পত্তির মূল্য নির্ধারণ' : 'Pricing Your Property',
          steps: language === 'bn' ? [
            'তালিকা উইজার্ডে এআই মূল্য নির্দেশিকা টুল ব্যবহার করুন',
            'গত ৬ মাসে ১ কিমির মধ্যে তুলনামূলক বিক্রয় পর্যালোচনা করুন',
            'প্রস্তাবিত মূল্য পরিসীমা বিবেচনা করুন — পরিসীমার মধ্যে মূল্য নির্ধারিত সম্পত্তি ৩০% দ্রুত বিক্রি হয়',
            'বিস্তারিত বাজার মূল্যায়নের জন্য ন্যায্য মূল্য বিশ্লেষণ টুল দেখুন',
            'তুলনামূলক বিক্রয়ে ধরা না পড়া অনন্য বৈশিষ্ট্যের জন্য সামঞ্জস্য করুন',
          ] : [
            'Use the AI Pricing Guidance tool in the listing wizard',
            'Review comparable sales within 1km over the past 6 months',
            'Consider the suggested price range — properties priced within range sell 30% faster',
            'Check the Fair Price Analysis tool for a detailed market valuation',
            'Adjust for unique features not captured in comparable sales',
          ]
        },
        {
          icon: 'Users',
          title: language === 'bn' ? 'লিড পরিচালনা' : 'Managing Leads',
          steps: language === 'bn' ? [
            'এজেন্ট ড্যাশবোর্ড বা তালিকার বিস্তারিত পৃষ্ঠা থেকে আপনার লিড অ্যাক্সেস করুন',
            'সেরা রূপান্তর হারের জন্য ২ ঘণ্টার মধ্যে জিজ্ঞাসার জবাব দিন',
            'লিড স্তর সিস্টেম ব্যবহার করুন: নতুন → যোগাযোগ করা হয়েছে → গরম → বন্ধ',
            'কথোপকথনের ইতিহাস ট্র্যাক করতে প্রতিটি লিডে নোট যোগ করুন',
            'প্ল্যাটফর্মের মাধ্যমে সরাসরি পরিদর্শন বুক করুন',
          ] : [
            'Access your leads from the Agent Dashboard or Listing Detail page',
            'Respond to enquiries within 2 hours for best conversion rates',
            'Use the lead stage system: New → Contacted → Hot → Closed',
            'Add notes to each lead to track conversation history',
            'Book inspections directly through the platform',
          ]
        },
      ]
    },
    agent: {
      title: language === 'bn' ? 'এজেন্ট রিসোর্স' : 'Agent Resources',
      description: language === 'bn' ? 'আপনার তালিকা পরিচালনা করুন, বাল্কে আপলোড করুন এবং পারফরম্যান্স অ্যানালিটিক্স ট্র্যাক করুন।' : 'Manage your listings, upload in bulk, and track performance analytics.',
      guides: [
        {
          icon: 'Upload',
          title: language === 'bn' ? 'বাল্ক সম্পত্তি আপলোড' : 'Bulk Property Upload',
          steps: language === 'bn' ? [
            'আপনার এজেন্ট ড্যাশবোর্ড থেকে বাল্ক আপলোড প্রপার্টিজে নেভিগেট করুন',
            'আমাদের CSV টেমপ্লেট ডাউনলোড করুন এবং আপনার সম্পত্তির ডেটা দিয়ে পূরণ করুন',
            'সম্পূর্ণ CSV আপলোড করুন — আমরা প্রতি ব্যাচে ১০০টি পর্যন্ত সম্পত্তি সমর্থন করি',
            'প্রকাশ করার আগে সমস্ত সম্পত্তি যাচাই করতে প্রিভিউ স্ক্রিন পর্যালোচনা করুন',
            'আপলোড অগ্রগতি পর্যবেক্ষণ করুন এবং যেকোনো যাচাইকরণ ত্রুটি ঠিক করুন',
          ] : [
            'Navigate to Bulk Upload Properties from your Agent Dashboard',
            'Download our CSV template and populate with your property data',
            'Upload the completed CSV — we support up to 100 properties per batch',
            'Review the preview screen to verify all properties before publishing',
            'Monitor upload progress and fix any validation errors flagged',
          ]
        },
        {
          icon: 'LayoutDashboard',
          title: language === 'bn' ? 'ড্যাশবোর্ড নেভিগেশন' : 'Dashboard Navigation',
          steps: language === 'bn' ? [
            'শীর্ষ নেভিগেশন মেনু থেকে আপনার এজেন্ট ড্যাশবোর্ড অ্যাক্সেস করুন',
            'মূল মেট্রিক্স দেখুন: সক্রিয় তালিকা, মোট ভিউ, এই মাসের লিড',
            'একটি জায়গায় আপনার সমস্ত সম্পত্তি পরিচালনা করতে তালিকা টেবিল ব্যবহার করুন',
            'অবস্থা অনুযায়ী তালিকা ফিল্টার করুন: ড্রাফট, লাইভ, বিক্রিত, আর্কাইভ করা',
            'দ্রুত একক সম্পত্তি তৈরির জন্য কুইক লিস্ট ব্যবহার করুন',
          ] : [
            'Access your Agent Dashboard from the top navigation menu',
            'View key metrics: active listings, total views, leads this month',
            'Use the Listings Table to manage all your properties in one place',
            'Filter listings by status: Draft, Live, Sold, Archived',
            'Use Quick List for fast single-property creation',
          ]
        },
        {
          icon: 'BarChart2',
          title: language === 'bn' ? 'পারফরম্যান্স অ্যানালিটিক্স' : 'Performance Analytics',
          steps: language === 'bn' ? [
            'তালিকার পারফরম্যান্স মেট্রিক্স দেখুন: ভিউ, জিজ্ঞাসা, পরিদর্শন বুকিং',
            'আপনার পোর্টফোলিও জুড়ে লিড রূপান্তর হার ট্র্যাক করুন',
            'বিভিন্ন সম্পত্তির ধরন এবং এলাকা জুড়ে পারফরম্যান্স তুলনা করুন',
            'ক্লায়েন্ট রিপোর্টিংয়ের জন্য CSV হিসেবে অ্যানালিটিক্স রিপোর্ট রপ্তানি করুন',
          ] : [
            'View listing performance metrics: views, enquiries, inspection bookings',
            'Track lead conversion rates across your portfolio',
            'Compare performance across different property types and suburbs',
            'Export analytics reports as CSV for client reporting',
          ]
        },
        {
          icon: 'MessageSquare',
          title: language === 'bn' ? 'ক্লায়েন্ট যোগাযোগ' : 'Client Communication',
          steps: language === 'bn' ? [
            'ক্লায়েন্ট কথোপকথনের জন্য প্রস্তুত হতে এজেন্ট প্রশ্ন টুল ব্যবহার করুন',
            'বিভিন্ন সম্পত্তির ধরনের জন্য পূর্ব-নির্মিত প্রশ্ন টেমপ্লেট অ্যাক্সেস করুন',
            'নির্দিষ্ট ক্লায়েন্ট পরিস্থিতির জন্য কাস্টম প্রশ্ন সেট সংরক্ষণ করুন',
            'প্ল্যাটফর্ম থেকে সরাসরি ইমেইলের মাধ্যমে ক্লায়েন্টদের সাথে প্রশ্ন সেট শেয়ার করুন',
          ] : [
            'Use the Agent Questions tool to prepare for client conversations',
            'Access pre-built question templates for different property types',
            'Save custom question sets for specific client scenarios',
            'Share question sets with clients via email directly from the platform',
          ]
        },
      ]
    },
    professional: {
      title: language === 'bn' ? 'পেশাদার গাইড' : 'Professional Guides',
      description: language === 'bn' ? 'আপনার প্রোফাইল অপ্টিমাইজ করুন, বুকিং পরিচালনা করুন এবং আপনার ক্লায়েন্ট বেস বাড়ান।' : 'Optimise your profile, manage bookings, and grow your client base.',
      guides: [
        {
          icon: 'UserCheck',
          title: language === 'bn' ? 'মার্কেটপ্লেস নিবন্ধন' : 'Marketplace Registration',
          steps: language === 'bn' ? [
            '"পেশাদার হিসেবে যোগ দিন" ক্লিক করুন এবং আপনার বিভাগ নির্বাচন করুন',
            'সমস্ত প্রোফাইল বিভাগ সম্পূর্ণ করুন — ১০০% সম্পূর্ণ প্রোফাইল ৩ গুণ বেশি বুকিং পায়',
            'আপনার পেশাদার সার্টিফিকেশন এবং লাইসেন্স ডকুমেন্ট আপলোড করুন',
            'আপনার সেবা এলাকা, বিশেষত্ব এবং মূল্য নির্ধারণ করুন',
            'যাচাইকরণের জন্য জমা দিন — অনুমোদন ১-৩ কার্যদিবস সময় নেয়',
          ] : [
            'Click "Join as Professional" and select your category',
            'Complete all profile sections — profiles with 100% completion get 3x more bookings',
            'Upload your professional certifications and licence documents',
            'Set your service areas, specialisations, and pricing',
            'Submit for verification — approval takes 1–3 business days',
          ]
        },
        {
          icon: 'Star',
          title: language === 'bn' ? 'প্রোফাইল অপ্টিমাইজেশন' : 'Profile Optimisation',
          steps: language === 'bn' ? [
            'আপনার দক্ষতা এবং অনন্য মূল্য তুলে ধরে একটি আকর্ষণীয় বায়ো লিখুন',
            'আপনার সেরা কাজ প্রদর্শন করে পোর্টফোলিও আইটেম যোগ করুন',
            'স্পষ্ট মূল্য এবং বিবরণ সহ সমস্ত সেবা তালিকাভুক্ত করুন',
            'সামাজিক প্রমাণ তৈরি করতে পূর্ববর্তী ক্লায়েন্টদের কাছ থেকে পর্যালোচনার অনুরোধ করুন',
            'দ্রুত বুকিংয়ের জন্য আপনার উপলব্ধতা ক্যালেন্ডার আপডেট রাখুন',
          ] : [
            'Write a compelling bio highlighting your expertise and unique value',
            'Add portfolio items showcasing your best work',
            'List all services with clear pricing and descriptions',
            'Request reviews from past clients to build social proof',
            'Keep your availability calendar updated for faster bookings',
          ]
        },
        {
          icon: 'Calendar',
          title: language === 'bn' ? 'বুকিং ব্যবস্থাপনা' : 'Booking Management',
          steps: language === 'bn' ? [
            'আপনার পেশাদার ড্যাশবোর্ডে আগত বুকিং অনুরোধ দেখুন',
            'আপনার প্রতিক্রিয়া হার বজায় রাখতে ২৪ ঘণ্টার মধ্যে বুকিং নিশ্চিত বা প্রত্যাখ্যান করুন',
            'প্রতিটি বুকিংয়ে নোট এবং ক্লায়েন্টের বিবরণ যোগ করুন',
            'পর্যালোচনার অনুরোধ ট্রিগার করতে বুকিং সম্পন্ন হিসেবে চিহ্নিত করুন',
            'সময়ের সাথে আপনার বুকিং ইতিহাস এবং আয় ট্র্যাক করুন',
          ] : [
            'View incoming booking requests in your Professional Dashboard',
            'Confirm or decline bookings within 24 hours to maintain your response rate',
            'Add notes and client details to each booking',
            'Mark bookings as completed to trigger review requests',
            'Track your booking history and earnings over time',
          ]
        },
        {
          icon: 'CreditCard',
          title: language === 'bn' ? 'পেমেন্ট প্রক্রিয়াকরণ' : 'Payment Processing',
          steps: language === 'bn' ? [
            'প্রোফাইল সেটিংসে আপনার পেমেন্ট পছন্দ নির্ধারণ করুন',
            'পেমেন্ট সরাসরি ক্লায়েন্টদের দ্বারা করা হয় — ListingLens পেমেন্ট প্রক্রিয়া করে না',
            'আপনার প্রোফাইল এবং সেবার বিবরণে আপনার পেমেন্টের শর্তাবলী স্পষ্টভাবে উল্লেখ করুন',
            'আপনার ব্যবসায়িক অনুশীলন অনুযায়ী সরাসরি ক্লায়েন্টদের কাছে চালান বা রসিদ ইস্যু করুন',
            'বিরোধের জন্য, মধ্যস্থতা সহায়তার জন্য support@listinglens.live-এ যোগাযোগ করুন',
          ] : [
            'Set your payment preferences in Profile Settings',
            'Payments are made directly by clients — ListingLens does not process payments',
            'Clearly state your payment terms in your profile and service descriptions',
            'Issue invoices or receipts directly to clients as per your business practice',
            'For disputes, contact support@listinglens.live for mediation assistance',
          ]
        },
      ]
    },
  };

  const content = guideContent?.[activeRole];

  const resources = [
    { icon: 'HelpCircle', label: t('faq'), description: language === 'bn' ? 'সাধারণ প্রশ্নের দ্রুত উত্তর' : 'Quick answers to common questions', path: '/faq', color: '#00D4FF' },
    { icon: 'MessageCircle', label: language === 'bn' ? 'সহায়তায় যোগাযোগ' : 'Contact Support', description: language === 'bn' ? 'আমাদের দল থেকে সাহায্য পান' : 'Get help from our team', path: '/contact-us', color: '#7B2FFF' },
    { icon: 'BookOpen', label: t('aboutUs'), description: language === 'bn' ? 'আমাদের প্ল্যাটফর্ম সম্পর্কে জানুন' : 'Learn about our platform', path: '/about-us', color: '#00D4FF' },
  ];

  const troubleshootingItems = [
    {
      issue: language === 'bn' ? 'বিশ্লেষণ লোড হচ্ছে না' : 'Analysis not loading',
      fix: language === 'bn' ? 'আপনার ইন্টারনেট সংযোগ পরীক্ষা করুন এবং রিফ্রেশ করার চেষ্টা করুন। সমস্যা অব্যাহত থাকলে ব্রাউজার ক্যাশ পরিষ্কার করুন।' : 'Check your internet connection and try refreshing. If the issue persists, clear browser cache.'
    },
    {
      issue: language === 'bn' ? 'ফটো আপলোড করা যাচ্ছে না' : 'Can\'t upload photos',
      fix: language === 'bn' ? 'নিশ্চিত করুন ফটোগুলি JPG/PNG/WEBP ফরম্যাটে এবং প্রতিটি ২০MB-এর কম। সমস্যা অব্যাহত থাকলে ভিন্ন ব্রাউজার ব্যবহার করুন।' : 'Ensure photos are JPG/PNG/WEBP format and under 20MB each. Try a different browser if issues persist.'
    },
    {
      issue: language === 'bn' ? 'লগইন সমস্যা' : 'Login issues',
      fix: language === 'bn' ? 'পাসওয়ার্ড রিসেট করতে "পাসওয়ার্ড ভুলে গেছেন" ব্যবহার করুন। যাচাইকরণ ইমেইলের জন্য স্প্যাম ফোল্ডার দেখুন। ইনকগনিটো মোড ব্যবহার করে দেখুন।' : 'Use "Forgot Password" to reset. Check spam folder for verification emails. Try incognito mode.'
    },
    {
      issue: language === 'bn' ? 'তালিকা দেখা যাচ্ছে না' : 'Listing not appearing',
      fix: language === 'bn' ? 'তালিকাগুলি লাইভ হতে ২-৪ ঘণ্টা সময় নেয়। পর্যালোচনার সময় কোনো সমস্যা চিহ্নিত হলে আপনার ইমেইল দেখুন।' : 'Listings take 2–4 hours to go live. Check your email for any issues flagged during review.'
    },
  ];

  const GuideCard = ({ guide }) => {
    const [open, setOpen] = useState(false);
    return (
      <div className="rounded-xl overflow-hidden transition-all" style={{ border: `1px solid ${open ? 'rgba(0,212,255,0.3)' : 'rgba(255,255,255,0.06)'}`, background: open ? 'rgba(0,212,255,0.03)' : 'rgba(255,255,255,0.02)' }}>
        <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-4 p-5 text-left">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(0,212,255,0.1)' }}>
            <Icon name={guide?.icon} size={18} color="#00D4FF" />
          </div>
          <span className="flex-1 font-semibold" style={{ color: '#F0F4FF' }}>{guide?.title}</span>
          <div className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-all" style={{ background: open ? 'rgba(0,212,255,0.15)' : 'rgba(255,255,255,0.05)', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>
            <Icon name="ChevronDown" size={16} color={open ? '#00D4FF' : 'rgba(240,244,255,0.5)'} />
          </div>
        </button>
        {open && (
          <div className="px-5 pb-5">
            <ol className="space-y-3">
              {guide?.steps?.map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: 'rgba(0,212,255,0.15)', color: '#00D4FF' }}>{i + 1}</span>
                  <span className="text-sm leading-relaxed" style={{ color: 'rgba(240,244,255,0.7)' }}>{step}</span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen" style={{ background: '#050A14' }}>
      <Helmet>
        <title>Help & Support | ListingLens – Australia & UAE Property Platform</title>
        <meta name="description" content="Get help with ListingLens. Find guides for buyers, sellers, agents, and professionals. Learn how to search properties, use AI analysis, list properties, and navigate Australian and UAE real estate markets." />
        <meta name="keywords" content="ListingLens help, property platform support, real estate help Australia, property listing help UAE, how to list property, AI property analysis help, real estate FAQ Australia UAE" />
        <link rel="canonical" href="https://listinglen3865.builtwithrocket.new/help-support" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://listinglen3865.builtwithrocket.new/help-support" />
        <meta property="og:title" content="Help & Support | ListingLens – Australia & UAE Property Platform" />
        <meta property="og:description" content="Get help with ListingLens. Find guides for buyers, sellers, agents, and professionals navigating Australian and UAE real estate markets." />
        <meta property="og:image" content="https://listinglen3865.builtwithrocket.new/assets/images/logo_LL-1772022913346.jpeg" />
        <meta property="og:site_name" content="ListingLens" />
        <meta property="og:locale" content="en_AU" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Help & Support | ListingLens – Australia & UAE Property Platform" />
        <meta name="twitter:description" content="Get help with ListingLens. Find guides for buyers, sellers, agents, and professionals navigating Australian and UAE real estate markets." />
        <meta name="twitter:image" content="https://listinglen3865.builtwithrocket.new/assets/images/logo_LL-1772022913346.jpeg" />
      </Helmet>
      {/* Nav */}
      <nav className="border-b border-border/30 px-4 md:px-8 h-16 flex items-center justify-between" style={{ background: 'rgba(5,10,20,0.95)', backdropFilter: 'blur(12px)' }}>
        <Link to="/" className="flex items-center gap-3">
          <img
            src="/assets/images/logo_LL-1772022913346.jpeg"
            alt="ListingLens logo"
            className="h-9 w-auto rounded-lg object-cover"
            style={{ boxShadow: '0 0 12px rgba(0,212,255,0.4)', border: '1px solid rgba(0,212,255,0.3)', maxWidth: '130px' }}
          />
        </Link>
        <div className="flex items-center gap-4">
          <GoBackButton fallbackPath="/" label={t('goBack')} />
          <Link to="/faq" className="text-sm text-muted-foreground hover:text-primary transition-colors">{t('faq')}</Link>
          <Link to="/contact-us" className="text-sm px-4 py-2 rounded-lg font-medium transition-colors" style={{ background: 'rgba(0,212,255,0.1)', color: '#00D4FF', border: '1px solid rgba(0,212,255,0.3)' }}>{t('contactUs')}</Link>
        </div>
      </nav>
      {/* Hero */}
      <section className="py-16 md:py-24 px-4 md:px-8 text-center">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6" style={{ background: 'rgba(0,212,255,0.1)', color: '#00D4FF', border: '1px solid rgba(0,212,255,0.2)' }}>
            <Icon name="BookOpen" size={14} />
            {t('helpSupportTitle')}
          </div>
          <h1 className="text-4xl md:text-5xl font-black mb-4" style={{ color: '#F0F4FF' }}>{t('howCanWeHelp')}</h1>
          <p className="text-lg" style={{ color: 'rgba(240,244,255,0.6)' }}>{t('helpSupportSubtitle')}</p>
        </div>
      </section>
      <div className="max-w-5xl mx-auto px-4 md:px-8 pb-20">
        {/* Role Selector */}
        <div className="flex flex-wrap gap-3 justify-center mb-12">
          {roles?.map((role) => (
            <button
              key={role?.id}
              onClick={() => setActiveRole(role?.id)}
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all"
              style={{
                background: activeRole === role?.id ? `${role?.color}15` : 'rgba(255,255,255,0.04)',
                color: activeRole === role?.id ? role?.color : 'rgba(240,244,255,0.6)',
                border: `1px solid ${activeRole === role?.id ? `${role?.color}40` : 'rgba(255,255,255,0.08)'}`,
                transform: activeRole === role?.id ? 'scale(1.05)' : 'scale(1)',
              }}
            >
              <Icon name={role?.icon} size={16} color={activeRole === role?.id ? role?.color : 'rgba(240,244,255,0.5)'} />
              {role?.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl font-bold mb-2" style={{ color: '#F0F4FF' }}>{content?.title}</h2>
          <p style={{ color: 'rgba(240,244,255,0.6)' }}>{content?.description}</p>
        </div>

        <div className="space-y-4 mb-16">
          {content?.guides?.map((guide) => (
            <GuideCard key={guide?.title} guide={guide} />
          ))}
        </div>

        {/* Troubleshooting */}
        <div className="mb-12 p-6 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <h3 className="text-lg font-bold mb-4" style={{ color: '#F0F4FF' }}>{t('commonTroubleshooting')}</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {troubleshootingItems?.map((item) => (
              <div key={item?.issue} className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <p className="font-medium text-sm mb-1" style={{ color: '#F0F4FF' }}>{item?.issue}</p>
                <p className="text-xs leading-relaxed" style={{ color: 'rgba(240,244,255,0.6)' }}>{item?.fix}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Resource Links */}
        <div className="grid md:grid-cols-3 gap-4">
          {resources?.map((res) => (
            <Link
              key={res?.label}
              to={res?.path}
              className="flex items-start gap-4 p-5 rounded-xl transition-all hover:scale-105"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${res?.color}15` }}>
                <Icon name={res?.icon} size={18} color={res?.color} />
              </div>
              <div>
                <p className="font-semibold text-sm" style={{ color: '#F0F4FF' }}>{res?.label}</p>
                <p className="text-xs mt-1" style={{ color: 'rgba(240,244,255,0.5)' }}>{res?.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
      <FooterSection />
    </div>
  );
};

export default HelpSupport;
